/* rimg lazy-loader / size-optimized hydrator.
   Ported from Pixel Union's rimg module in live editions.js.
   - Finds img elements with data-rimg="lazy" + data-rimg-template
   - Measures actual rendered size when the image enters the viewport
   - Builds a size-matched srcset (multiple density variants)
   - Toggles data-rimg attribute lazy -> loading -> loaded so the live
     [data-rimg=*] opacity rules in atelier-page.css can fade it in
   The accompanying CSS is in assets/atelier-page.css. */
(function () {
  'use strict';

  var MULTIPLE = 32;        // round to multiples of 32px (matches Shopify CDN cache buckets)
  var DENSITIES = [1, 1.5, 2, 3];

  function roundSize(size) {
    if (size <= 0) return MULTIPLE;
    return Math.ceil(size / MULTIPLE) * MULTIPLE;
  }

  function getElementSize(el) {
    // Measure the image element itself, exactly as live's rimg does. The
    // gallery image is wrapped in an inline-block zoom span and capped by
    // max-width:100%, so its width is already min(server-rendered ~700px,
    // gallery column) — i.e. the true display box. Do NOT measure the column
    // (.product-gallery--viewport): at very low DPR (e.g. 1920 @ 33% zoom) the
    // column can be ~1600px while the source maxes at 1080, and sizing to the
    // column would upscale the first image far larger than live renders it.
    // (The previously-suspected lazy "small file" issue is handled by the
    // fractional-density srcset in buildSrcset, not by the measurement.)
    var node = el;
    var w = node.clientWidth;
    var h = node.clientHeight;
    // Walk up the tree if the element doesn't yet have a size of its own
    while ((w === 0 || h === 0) && node.parentElement) {
      node = node.parentElement;
      w = node.clientWidth;
      h = node.clientHeight;
    }
    return { width: w, height: h };
  }

  function parseMax(str) {
    if (!str) return { width: Infinity, height: Infinity };
    var parts = str.split('x');
    return {
      width: parseInt(parts[0], 10) || Infinity,
      height: parseInt(parts[1], 10) || Infinity
    };
  }

  function buildSrcset(el) {
    var template = el.getAttribute('data-rimg-template');
    var max = parseMax(el.getAttribute('data-rimg-max'));
    var crop = el.getAttribute('data-rimg-crop');
    var scale = parseInt(el.getAttribute('data-rimg-scale') || '1', 10) || 1;
    var box = getElementSize(el);

    if (!template) return null;

    var hasCrop = crop && crop !== 'false' && crop !== 'null' && crop !== '';
    var baseW = box.width * scale;
    var baseH = box.height * scale;
    var parts = [];
    var seenWidths = {};
    DENSITIES.forEach(function (d) {
      var w = Math.min(roundSize(baseW * d), max.width);
      var h = Math.min(roundSize(baseH * d), max.height);
      // The requested width is capped at the source's intrinsic size
      // (data-rimg-max). When that cap kicks in, the file is smaller than
      // box*d, so its TRUE density is w/baseW — not the nominal d. Emitting the
      // nominal d made the browser render a capped file at intrinsic/d, which is
      // SMALLER than the box (a 720px-max model shot in a 470 box at 2x rendered
      // at 360px; the inline-block zoom wrapper then shrink-wrapped to it, so
      // the image showed smaller than the uncapped packshot). Live emits the
      // true fractional density (e.g. _714x714 1.02x) — match that. Skip
      // duplicate widths so we don't emit two entries with the same density.
      if (seenWidths[w]) return;
      seenWidths[w] = true;
      var density = baseW > 0 ? Math.round((w / baseW) * 100) / 100 : d;
      var sizeToken = hasCrop ? (w + 'x' + h) : (w + 'x');
      parts.push(template.replace('{size}', sizeToken) + ' ' + density + 'x');
    });
    return parts.join(', ');
  }

  function load(el) {
    var newSrcset = buildSrcset(el);
    if (!newSrcset) {
      el.setAttribute('data-rimg', 'error');
      return;
    }

    el.setAttribute('data-rimg', 'loading');

    function onLoad() {
      el.setAttribute('data-rimg', 'loaded');
      cleanup();
    }
    function onError() {
      el.setAttribute('data-rimg', 'error');
      cleanup();
    }
    function cleanup() {
      el.removeEventListener('load', onLoad);
      el.removeEventListener('error', onError);
    }
    el.addEventListener('load', onLoad);
    el.addEventListener('error', onError);

    el.setAttribute('srcset', newSrcset);

    // If the image was cached and "load" already fired, mark loaded manually.
    if (el.complete && el.naturalWidth > 0) {
      onLoad();
    }
  }

  function init() {
    var nodes = document.querySelectorAll('img[data-rimg="lazy"]');
    if (!nodes.length) return;

    if (!('IntersectionObserver' in window)) {
      nodes.forEach(load);
      return;
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        io.unobserve(entry.target);
        load(entry.target);
      });
    }, { rootMargin: '200px 0px', threshold: 0.01 });

    nodes.forEach(function (n) { io.observe(n); });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Expose for scripts that inject new product images (load-more, AJAX filter)
  window.Rimg = window.Rimg || {};
  window.Rimg.init = init;
})();
